import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, User, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import DOMPurify from "dompurify";

interface BlogPostData {
  id: string;
  title: string;
  content: string;
  featured_image_url: string | null;
  category: string;
  author: string;
  published_at: string;
}

const BlogPost = () => {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      fetchPost();
    }
  }, [slug, language]);

  const fetchPost = async () => {
    try {
      setIsLoading(true);
      
      const { data: postData, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          blog_posts_translations (*)
        `)
        .eq('slug', slug)
        .eq('published', true)
        .single();

      if (error) throw error;

      const translation = postData.blog_posts_translations?.find(
        (t: any) => t.language === language
      );

      setPost({
        id: postData.id,
        title: translation?.title || postData.title,
        content: translation?.content || postData.content,
        featured_image_url: postData.featured_image_url,
        category: postData.category,
        author: translation?.author || postData.author,
        published_at: postData.published_at,
      });
    } catch (error) {
      console.error("Error fetching blog post:", error);
      toast({
        title: t("blog.error"),
        description: t("blog.postNotFound"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="container mx-auto px-4 py-24">
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t("blog.loading")}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="container mx-auto px-4 py-24">
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t("blog.postNotFound")}</p>
            <Link to="/blog">
              <Button className="mt-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("blog.backToBlog")}
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto px-4 py-24">
        <article className="max-w-4xl mx-auto">
          <Link to="/blog">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("blog.backToBlog")}
            </Button>
          </Link>

          {post.featured_image_url && (
            <div className="aspect-video overflow-hidden rounded-lg mb-8">
              <img
                src={post.featured_image_url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="mb-6">
            <Badge variant="secondary" className="mb-4">
              {post.category}
            </Badge>
            <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
            
            <div className="flex items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {post.author}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(post.published_at).toLocaleDateString(
                  language === "ru" ? "ru-RU" : language === "es" ? "es-ES" : "en-US",
                  { year: 'numeric', month: 'long', day: 'numeric' }
                )}
              </div>
            </div>
          </div>

          <div 
            className="prose prose-lg max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
          />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
